import React, { useState, useEffect } from "react";
import { Lightning, TrendUp, Database, ChartLineUp, CaretDown } from "@phosphor-icons/react";
import { useGlobalData } from "@/lib/dataStreams";

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-800 group bg-slate-900/20 hover:bg-slate-900/40 transition-colors px-6 rounded-2xl mb-4">
      <button 
        onClick={() => setOpen(!open)} 
        className="w-full py-6 flex justify-between items-center text-left"
      >
        <span className="font-bold text-lg text-slate-200 group-hover:text-purple-400 transition-colors pr-4">{q}</span>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${open ? 'bg-purple-500/20 text-purple-400' : 'bg-slate-800 text-slate-400'}`}>
          <CaretDown size={16} className={`transform transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${open ? 'max-h-96 opacity-100 pb-6' : 'max-h-0 opacity-0'}`}>
        <p className="text-slate-300 leading-relaxed text-sm sm:text-base pr-8">
          {a}
        </p>
      </div>
    </div>
  );
};

export default function AIComboBot() {
  const { solPrice: price } = useGlobalData();
  const [allocations, setAllocations] = useState({ arb: 12, mig: 38, mom: 50 });

  useEffect(() => {
    const interval = setInterval(() => {
      // Arbitrage is capped at 15% of capital; Migration and Momentum
      // split the remainder, each staying between 30% and the rest.
      const arb = Math.floor(Math.random() * 11) + 5; // 5-15%
      const remaining = 100 - arb;
      const mig = Math.floor(Math.random() * (remaining - 60 + 1)) + 30; // 30..(remaining-30)
      const mom = remaining - mig;
      setAllocations({ arb, mig, mom });
    }, 3000); // Fast dynamic switching for the UI
    return () => clearInterval(interval);
  }, []);

  const faqs = [
    {
      q: "What is Risk-Parity Orchestration?",
      a: "Risk-parity orchestration means the system never leaves all its capital exposed to a single trading strategy. It dynamically evaluates the global network state—volatility, overall volume, and DEX liquidity depth—and automatically shifts capital to whichever strategy carries the lowest risk profile for the current conditions."
    },
    {
      q: "How does the Synergy Engine allocate funds?",
      a: "If the market is experiencing massive volatility (e.g., SOL moving 5% in an hour), the AI shifts weight towards Momentum. If the market is completely flat and sideways, it leans on Cross-Exchange Arbitrage (up to its 15% cap) to steadily harvest micro-spreads with zero directional risk."
    },
    {
      q: "What are the typical allocation boundaries?",
      a: "By design, Arbitrage is capped at 15% of total execution capital — spreads are consistent but small, so over-allocating there drags overall returns. Migration and Momentum share the remainder, each holding at least 30%. This enforced boundary keeps the architecture diversified and protected against isolated contract failures."
    },
    {
      q: "Is manual intervention required?",
      a: "No, the master node is fully autonomous. It constantly re-evaluates DEX liquidity depth and CEX order books 24/7 without sleep, neutralizing the emotional biases that affect human traders."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-purple-500/30 pb-20 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-purple-500/10 rounded-full blur-[150px] pointer-events-none" />
      
      <div className="max-w-[1500px] mx-auto px-4 md:px-8 py-16 relative z-10">
        <div className="mb-16 border-b border-slate-800 pb-12 text-center">
          <div className="w-20 h-20 mx-auto bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center mb-8 shadow-2xl shadow-purple-500/30">
            <Lightning size={40} color="#FFF" weight="fill" />
          </div>
          <span className="text-purple-400 font-bold text-xs uppercase tracking-wider bg-purple-500/10 px-3 py-1.5 rounded-full border border-purple-500/20">// Master Module 04</span>
          <h1 className="text-5xl sm:text-7xl font-extrabold tracking-tight mt-6 text-slate-100">
            AI Synergy
          </h1>
          <p className="text-slate-300 mt-8 max-w-3xl mx-auto text-lg leading-relaxed">
            The pinnacle of the B3eye architecture. This master node dynamically allocates computational resources and simulated capital across Arbitrage, Migration Sniping, and Momentum Trading based on real-time market volatility and liquidity metrics.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8 mb-16">
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 text-center shadow-sm">
            <div className="w-16 h-16 mx-auto bg-indigo-500/10 rounded-2xl flex items-center justify-center mb-6">
              <TrendUp size={32} className="text-indigo-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-200 mb-2">Arbitrage Allocation</h3>
            <div className="font-mono font-extrabold text-5xl text-indigo-400 my-4 transition-all duration-500">{allocations.arb}%</div>
            <p className="text-sm text-slate-500 mt-2 font-medium">Deployed during high CEX/DEX desync.</p>
          </div>
          
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 text-center shadow-sm">
            <div className="w-16 h-16 mx-auto bg-teal-500/10 rounded-2xl flex items-center justify-center mb-6">
              <Database size={32} className="text-teal-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-200 mb-2">Migration Allocation</h3>
            <div className="font-mono font-extrabold text-5xl text-teal-400 my-4 transition-all duration-500">{allocations.mig}%</div>
            <p className="text-sm text-slate-500 mt-2 font-medium">Deployed on Pump.fun volume spikes.</p>
          </div>
          
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 text-center shadow-sm">
            <div className="w-16 h-16 mx-auto bg-rose-500/10 rounded-2xl flex items-center justify-center mb-6">
              <ChartLineUp size={32} className="text-rose-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-200 mb-2">Momentum Allocation</h3>
            <div className="font-mono font-extrabold text-5xl text-rose-400 my-4 transition-all duration-500">{allocations.mom}%</div>
            <p className="text-sm text-slate-500 mt-2 font-medium">Deployed during steady trend formations.</p>
          </div>
        </div>

        <div className="bg-slate-900/50 backdrop-blur-xl border border-slate-700 rounded-3xl p-10 shadow-2xl relative overflow-hidden mb-20">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
          <h3 className="text-sm font-bold uppercase tracking-wider text-slate-300 mb-8 flex items-center gap-3 relative z-10">
            <span className="w-3 h-3 bg-purple-400 rounded-full animate-pulse shadow-[0_0_12px_rgba(192,132,252,0.8)]" /> 
            System Diagnostics
          </h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 font-sans relative z-10">
            <div>
              <div className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-2">Network Base Asset</div>
              <div className="text-slate-200 font-bold text-xl">SOL @ <span className="text-indigo-400 font-mono">{price ? `$${price.toFixed(3)}` : "..."}</span></div>
            </div>
            <div>
              <div className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-2">TPS Monitor</div>
              <div className="text-slate-200 font-bold text-xl font-mono">3,241 <span className="text-sm text-slate-500 font-sans">tx/s</span></div>
            </div>
            <div>
              <div className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-2">RPC Latency</div>
              <div className="text-emerald-400 font-bold text-xl font-mono">18ms <span className="text-sm text-emerald-400/60 font-sans">(Optimal)</span></div>
            </div>
            <div>
              <div className="text-slate-500 text-xs font-bold uppercase tracking-wider mb-2">Model Version</div>
              <div className="text-slate-200 font-bold text-xl font-mono bg-slate-800 px-3 py-1 rounded-lg inline-block border border-slate-700">B3-CORE-V2.1</div>
            </div>
          </div>
        </div>

        {/* FAQ SECTION */}
        <div className="max-w-[900px] mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-slate-100 mb-2">Synergy Engine Knowledge Base</h2>
            <p className="text-slate-300">Technical insights into the master orchestrator.</p>
          </div>
          <div className="space-y-2">
            {faqs.map((faq, i) => (
              <FaqItem key={i} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
